import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Slider,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { motion } from "framer-motion";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const evaluationCriteria = [
  {
    key: "originality",
    label: "Originality",
    detail:
      "This criterion assesses how novel and innovative the ideas in the paper are.",
  },
  {
    key: "significance",
    label: "Significance",
    detail:
      "This criterion evaluates the impact and importance of the paper in its academic field or real-world applications.",
  },
  {
    key: "methodology",
    label: "Methodology",
    detail:
      "This criterion examines the appropriateness and rigor of the methods or experiments employed in the paper.",
  },
  {
    key: "clarity",
    label: "Clarity",
    detail:
      "This criterion assesses how clearly the paper is written and how well its figures and tables convey information.",
  },
  {
    key: "futurePotential",
    label: "Future Potential",
    detail:
      "This criterion evaluates the potential for future development, applications, or further research based on the paper's findings.",
  },
];

const PaperEvaluationForm = () => {
  // Scores for each criterion (0 to 4)
  const [scores, setScores] = useState({
    originality: 0,
    significance: 0,
    methodology: 0,
    clarity: 0,
    futurePotential: 0,
  });
  const [comment, setComment] = useState("");
  const [openModalKey, setOpenModalKey] = useState(null);

  // Calculate total score (out of 20)
  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);

  const handleSliderChange = (key) => (event, newValue) => {
    setScores((prev) => ({ ...prev, [key]: newValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Evaluation submitted:", { scores, comment, totalScore });
    alert(`Evaluation submitted!\nTotal Score: ${totalScore}/20`);
  };

  const handleOpenModal = (key) => {
    setOpenModalKey(key);
  };

  const handleCloseModal = () => {
    setOpenModalKey(null);
  };

  // Retrieve current criterion detail to show in the modal
  const currentDetail =
    evaluationCriteria.find((item) => item.key === openModalKey) || {};

  return (
    <Container
      maxWidth="sm"
      style={{
        maxHeight: "70vh",
        overflowY: "auto",
        background: "linear-gradient(135deg, #121212 0%, #1a1a2e 100%)",
        padding: "20px",
        borderRadius: "20px",
        color: "white",
        textAlign: "center",
        border: "1px solid rgba(0, 255, 255, 0.3)",
        boxShadow: "0px 0px 10px rgba(0, 255, 255, 0.5)",
      }}
    >
      <Typography variant="h5" gutterBottom>
        Paper Evaluation
      </Typography>

      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {evaluationCriteria.map((criteria) => (
          <Box key={criteria.key} mb={3}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <Typography variant="subtitle1">
                {criteria.label}
              </Typography>
              <IconButton
                aria-label="help"
                size="small"
                onClick={() => handleOpenModal(criteria.key)}
                style={{ marginLeft: "5px", color: "#00ffcc" }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <Slider
              value={scores[criteria.key]}
              onChange={handleSliderChange(criteria.key)}
              aria-labelledby="discrete-slider"
              valueLabelDisplay="auto"
              step={1}
              marks
              min={0}
              max={4}
              sx={{
                color: "#00ffcc",
              }}
            />
          </Box>
        ))}

        <Box mb={2}>
          <Typography variant="subtitle1">
            Total Score: {totalScore} / 20
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Evaluation Comments"
          multiline
          rows={4}
          placeholder="For example: This paper is highly creative and shows promising future potential..."
          variant="outlined"
          margin="dense"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          InputLabelProps={{ style: { color: "#bbb" } }}
          InputProps={{
            style: {
              backgroundColor: "#1e1e1e",
              borderRadius: "10px",
              color: "white",
            },
          }}
          sx={{ mt: 1 }}
        />

        <Button
          type="submit"
          variant="contained"
          style={{
            marginTop: "15px",
            backgroundColor: "#00ffcc",
            color: "black",
            borderRadius: "10px",
          }}
        >
          Submit Evaluation
        </Button>
      </motion.form>

      {/* Dialog for displaying criterion details */}
      <Dialog
        open={Boolean(openModalKey)}
        onClose={handleCloseModal}
        aria-labelledby="criterion-dialog-title"
        aria-describedby="criterion-dialog-description"
        PaperProps={{
          style: {
            backgroundColor: "#1a1a2e",
            color: "white",
            borderRadius: "15px",
          },
        }}
      >
        <DialogTitle id="criterion-dialog-title">
          {currentDetail.label}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="criterion-dialog-description">
            {currentDetail.detail}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            style={{
              backgroundColor: "#00ffcc",
              color: "black",
              borderRadius: "10px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaperEvaluationForm;
